// Sign-in for the tablet. The API's bearer tokens are used directly (there is no web server
// in between): a 10-minute access token and a single-use refresh token, rotated on every
// refresh. Reusing a spent refresh token ends the whole session on the server, so refreshes
// are single-flight in this tab and serialised across tabs with a Web Lock.
//
// The session is kept in IndexedDB so the app opens signed in with no network; the rep's
// drafts never depend on it.

import type { Schemas } from "@artisan/api-client";

import { apiUrl } from "./env";
import { db } from "./db";

export type User = Schemas["UserOut"];
export type MyOrg = Schemas["MyOrgOut"];

export interface Session {
  user: User;
  active_org_id: string | null;
  orgs: MyOrg[];
  access_token: string;
  /** ms since epoch */
  access_expires_at: number;
  refresh_token: string;
}

export type AuthState =
  | { status: "loading" }
  | { status: "signed_out"; reason?: "expired" }
  | { status: "signed_in"; session: Session };

const KEY = "session";
let state: AuthState = { status: "loading" };
const listeners = new Set<() => void>();

function set(next: AuthState) {
  state = next;
  for (const l of listeners) l();
}

export const auth = {
  get: () => state,
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

export function currentSession(): Session | null {
  return state.status === "signed_in" ? state.session : null;
}

export function activeOrg(session: Session): MyOrg | null {
  return session.orgs.find((o) => o.id === session.active_org_id) ?? null;
}

export async function loadSession(): Promise<void> {
  const saved = await db.get<Session>("kv", KEY).catch(() => undefined);
  set(saved ? { status: "signed_in", session: saved } : { status: "signed_out" });
}

async function save(session: Session) {
  await db.put("kv", session, KEY);
  set({ status: "signed_in", session });
}

export class AuthError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(code);
    this.name = "AuthError";
  }
}

async function post<T>(path: string, body: unknown, token?: string): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method: "POST",
    headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new AuthError((data as { error?: { code?: string } }).error?.code ?? "http_error", res.status);
  return data as T;
}

async function fetchOrgs(token: string): Promise<MyOrg[]> {
  const res = await fetch(apiUrl("/v1/me/organizations"), { headers: { authorization: `Bearer ${token}` } });
  if (!res.ok) return [];
  return (await res.json()) as MyOrg[];
}

async function fromTokens(t: Schemas["TokenOut"], previous?: Session): Promise<Session> {
  if (!t.refresh_token) throw new AuthError("no_refresh_token", 500);
  return {
    user: t.user,
    active_org_id: t.active_org_id,
    orgs: previous && previous.active_org_id === t.active_org_id ? previous.orgs : await fetchOrgs(t.access_token),
    access_token: t.access_token,
    access_expires_at: Date.now() + t.expires_in * 1000,
    refresh_token: t.refresh_token,
  };
}

export type LoginOutcome = { kind: "done" } | { kind: "mfa"; mfa_token: string };

export async function login(email: string, password: string): Promise<LoginOutcome> {
  const r = await post<Schemas["TokenOut"] | Schemas["MfaChallengeOut"]>("/v1/auth/login", { email, password });
  if ("mfa_token" in r && !("access_token" in r)) return { kind: "mfa", mfa_token: r.mfa_token };
  await save(await fromTokens(r as Schemas["TokenOut"]));
  return { kind: "done" };
}

export async function loginMfa(mfa_token: string, code: string): Promise<void> {
  const body = /^\d{6}$/.test(code.trim()) ? { mfa_token, code: code.trim() } : { mfa_token, recovery_code: code.trim() };
  await save(await fromTokens(await post<Schemas["TokenOut"]>("/v1/auth/login/2fa", body)));
}

export async function switchOrg(org_id: string): Promise<void> {
  const s = currentSession();
  if (!s) return;
  const token = await accessToken();
  if (!token) return;
  const t = await post<Schemas["TokenOut"]>("/v1/auth/switch-org", { org_id }, token);
  const next = await fromTokens(t);
  await save(next);
}

export async function logout(): Promise<void> {
  const s = currentSession();
  await db.delete("kv", KEY);
  set({ status: "signed_out" });
  if (s) {
    // Best effort: the server ends the session; offline, the token simply expires.
    fetch(apiUrl("/v1/auth/logout"), {
      method: "POST",
      headers: { authorization: `Bearer ${s.access_token}` },
    }).catch(() => undefined);
  }
}

let refreshing: Promise<Session | null> | null = null;

async function withLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  if (!locks) return fn();
  return locks.request("artisan-refresh", fn) as Promise<T>;
}

/** Renew the access token. Returns null when the session is over (sign in again). */
export function refresh(): Promise<Session | null> {
  refreshing ??= withLock(async () => {
    // Another tab may have refreshed while this one waited for the lock.
    const stored = await db.get<Session>("kv", KEY);
    if (!stored) return null;
    if (stored.access_expires_at - Date.now() > 60_000 && stored.access_token !== currentSession()?.access_token) {
      set({ status: "signed_in", session: stored });
      return stored;
    }
    try {
      const t = await post<Schemas["TokenOut"]>("/v1/auth/refresh", { refresh_token: stored.refresh_token });
      const next = await fromTokens(t, stored);
      await save(next);
      return next;
    } catch (e) {
      if (e instanceof AuthError && (e.status === 401 || e.status === 403)) {
        await db.delete("kv", KEY);
        set({ status: "signed_out", reason: "expired" });
        return null;
      }
      throw e; // offline or a server error: keep the session, try later
    }
  }).finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/** A valid access token, renewed when it is about to expire. */
export async function accessToken(): Promise<string | null> {
  const s = currentSession();
  if (!s) return null;
  if (s.access_expires_at - Date.now() > 30_000) return s.access_token;
  const next = await refresh();
  return next?.access_token ?? null;
}

/** fetch with the bearer token; a 401 renews once and retries. */
export async function authFetch(input: Request): Promise<Response> {
  const retry = input.clone();
  const token = await accessToken();
  if (token) input.headers.set("authorization", `Bearer ${token}`);
  const res = await fetch(input);
  if (res.status !== 401 || !token) return res;
  const next = await refresh();
  if (!next) return res;
  retry.headers.set("authorization", `Bearer ${next.access_token}`);
  return fetch(retry);
}
